import React, { useState, useEffect, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  Stack,
} from "@mui/material";
import { useAssistantStore, readBudLaunch } from "../../../stores/assistant";
import { useAuthenticationStore } from "../../../stores/auth";
import { enqueueSnackbar } from "notistack";
import { Spa, Add, ArrowBack } from "@mui/icons-material";
import { FruitifyContainer } from "../../../shared-components/Containers";
import GeneralLoading from "../../../shared-components/Loading";
import TieredFeatureAccess from "../../../shared-components/TieredFeatureAccess";
import { AssistantChat } from "../AssistantChat";
import { ConversationList } from "../components/ConversationList";
import { EditableTitle } from "../components/EditableTitle";
import { ConversationSidebar } from "../components/ConversationSidebar";
import { withPageTracking } from "../../../services/postHog";
import { useApiClient } from "../../../api";
import FruitifyIcon from "../../../shared-components/FruitifyIcons";
import { BetaFeatureChip } from "../../../shared-components/BetaFeature";

const AssistantPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const apiClient = useApiClient();
  const actor = useAuthenticationStore(
    (state) =>
      `${state.user.info?.id}:${state.user.signInSession}:${state.user.info?.activeContext?.roleAssignment?.id}`,
  );
  const actorRef = useRef(actor);
  actorRef.current = actor;
  const conversationId = searchParams.get("conversation");
  const isNewConversation = searchParams.get("new") === "true";
  const storedLaunch = useAssistantStore((state) => state.fullPageLaunch);
  const creationKind = searchParams.get("create");
  const fullPageLaunch =
    searchParams.get("launch") === "true" || creationKind
      ? storedLaunch || readBudLaunch(actor)
      : null;

  const [conversations, setConversations] = useState([]);
  const [conversationsLoading, setConversationsLoading] = useState(false);
  const [loadedConversation, setConversation] = useState(null);
  const conversation =
    loadedConversation?.requestedId === conversationId &&
    loadedConversation?.owner === actor
      ? loadedConversation
      : null;
  const [conversationError, setConversationError] = useState(null);
  const fetchGeneration = useRef(0);
  const [conversationLoading, setConversationLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [usageStats, setUsageStats] = useState(null);
  const pageSize = 10;

  useEffect(() => {
    setConversations([]);
    setPage(0);
    setUsageStats(null);
  }, [actor]);

  // Fetch usage stats
  useEffect(() => {
    loadUsageStats();
  }, [actor]);

  // Fetch conversation list
  useEffect(() => {
    loadConversations();
  }, [page, actor]);

  // Fetch specific conversation
  useEffect(() => {
    const generation = ++fetchGeneration.current;
    setConversation(null);
    setConversationError(null);
    if (conversationId) loadConversation(conversationId, generation);
    else setConversationLoading(false);
    return () => {
      fetchGeneration.current += 1;
    };
  }, [conversationId, actor]);

  const loadConversations = async (requestedPage = page) => {
    const owner = actor;
    setConversationsLoading(true);
    try {
      const data = await apiClient.assistant.getMyConversations({
        first: pageSize,
        offset: requestedPage * pageSize,
      });
      if (actorRef.current !== owner) return;
      if (requestedPage === 0) {
        setConversations(data || []);
      } else {
        setConversations((prev) => [...prev, ...(data || [])]);
      }
      setHasMore(data && data.length === pageSize);
    } catch (error) {
      console.error("Failed to load conversations:", error);
    } finally {
      setConversationsLoading(false);
    }
  };

  const loadUsageStats = async () => {
    try {
      const stats = await apiClient.assistant.getUsageStats();
      setUsageStats(stats);
    } catch (error) {
      console.error("Failed to load usage stats:", error);
    }
  };

  const loadConversation = async (id, generation) => {
    setConversationLoading(true);
    try {
      const data = await apiClient.assistant.getConversation(id);
      if (generation !== fetchGeneration.current) return;
      setConversation(data ? { ...data, requestedId: id, owner: actor } : null);
      if (!data) setConversationError("This conversation is not available.");
    } catch (error) {
      if (generation === fetchGeneration.current)
        setConversationError(
          "Unable to load this conversation. Please reload to try again.",
        );
    } finally {
      if (generation === fetchGeneration.current) setConversationLoading(false);
    }
  };

  const refetchConversations = () => {
    setPage(0);
    loadConversations(0);
  };

  const handleNewConversation = () => {
    useAssistantStore.getState().setFullPageLaunch(null);
    setConversation(null);
    setSearchParams({ new: "true" });
  };

  const handleBackToList = () => {
    navigate("/assistant");
  };

  const handleTitleSave = async (newTitle) => {
    try {
      const result = await apiClient.assistant.updateConversationTitle({
        conversationId,
        title: newTitle,
      });

      if (result.success) {
        // Update local state immediately
        setConversation((prev) => ({
          ...prev,
          title: newTitle,
        }));
        // Refresh list in background
        refetchConversations();
        enqueueSnackbar("Conversation title updated", { variant: "success" });
        return true;
      } else {
        enqueueSnackbar(result.error || "Could not update the title", {
          variant: "error",
        });
        return false;
      }
    } catch (error) {
      enqueueSnackbar("Could not update the title", { variant: "error" });
      return false;
    }
  };

  const handleLoadMore = () => {
    setPage((prev) => prev + 1);
  };

  const handleToggleSidebar = () => {
    setSidebarOpen((prev) => !prev);
  };

  // Show conversation list view
  if (!conversationId && !isNewConversation) {
    return (
      <FruitifyContainer data-testid="assistant-page">
        <Box sx={{ mb: 3 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: { xs: "flex-start", sm: "center" },
              justifyContent: "space-between",
              gap: 2,
              mb: 1,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: { xs: 1, sm: 2 },
                flexWrap: "wrap",
              }}
            >
              <Spa fontSize="large" color="secondary" />
              <Typography variant="h4">Ask Bud</Typography>
              <BetaFeatureChip />
            </Box>
            <TieredFeatureAccess
              accessOverride={usageStats?.canCreateNewConversation}
              tooltipMessage="Upgrade to create new conversations. Free tier is limited to assignment-specific queries."
              inlineButton={true}
            >
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={handleNewConversation}
              >
                New Conversation
              </Button>
            </TieredFeatureAccess>
          </Box>
          <Typography variant="body2" color="text.secondary">
            Browse your conversation history or start a new chat
          </Typography>

          {usageStats?.tier === "FREE" && (
            <Box
              sx={{ mt: 2, p: 2, bgcolor: "warning.light", borderRadius: 1 }}
            >
              <Typography variant="body2">
                Free Tier: {usageStats.responseCount} /{" "}
                {usageStats.responseLimit} AI responses used this week
                {usageStats.limitReached &&
                  " - Limit reached. Upgrade to continue."}
              </Typography>
            </Box>
          )}
        </Box>

        <ConversationList
          conversations={conversations}
          loading={conversationsLoading}
          hasMore={hasMore}
          onLoadMore={handleLoadMore}
        />
      </FruitifyContainer>
    );
  }

  // Show chat view (new or existing conversation)
  const conversationTitle = conversation?.title || "Untitled Conversation";

  return (
    <FruitifyContainer data-testid="assistant-page" maxWidth={false}>
      <Box sx={{ mb: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <IconButton
            onClick={handleBackToList}
            aria-label="Back to conversations"
          >
            <ArrowBack />
          </IconButton>
          <IconButton
            onClick={handleToggleSidebar}
            aria-label="Toggle conversation sidebar"
            color={sidebarOpen ? "primary" : "default"}
          >
            <FruitifyIcon name="Sidebar" />
          </IconButton>
          <Spa fontSize="large" color="secondary" />
          {conversationLoading ? (
            <Typography variant="h5">Loading...</Typography>
          ) : conversationId && conversation ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <EditableTitle
                title={conversationTitle}
                onSave={handleTitleSave}
                variant="h5"
              />
              <BetaFeatureChip />
            </Box>
          ) : (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="h5">Untitled Conversation</Typography>
              <BetaFeatureChip />
            </Box>
          )}
        </Stack>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          height: { xs: "auto", md: "calc(100dvh - 150px)" },
          minHeight: { xs: "65dvh", md: 0 },
          gap: 2,
        }}
      >
        {/* Sidebar */}
        {sidebarOpen && (
          <ConversationSidebar
            conversations={conversations}
            loading={conversationsLoading}
            currentConversationId={conversationId}
            onNewConversation={handleNewConversation}
            hasMore={hasMore}
            onLoadMore={handleLoadMore}
            usageStats={usageStats}
          />
        )}

        {/* Chat Area */}
        <Paper
          elevation={2}
          sx={{
            flex: 1,
            minHeight: { xs: "65dvh", md: 0 },
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {conversationLoading ? (
            <Box sx={{ p: 4 }}>
              <GeneralLoading />
            </Box>
          ) : conversationError || conversation?.isAccessible === false ? (
            <Typography sx={{ p: 3 }} role="status">
              {conversationError ||
                "This conversation is locked for your current account. Open Bud from an available assignment to start a new conversation."}
            </Typography>
          ) : (
            <AssistantChat
              key={conversationId || "new"}
              initialContext={{
                ...(!conversationId
                  ? fullPageLaunch ||
                    (["assignment", "quiz"].includes(creationKind)
                      ? { creation: { kind: creationKind } }
                      : {})
                  : {}),
                class_id:
                  conversation?.focusedClassId ||
                  (!conversationId ? fullPageLaunch?.class_id : null),
                assignment_id:
                  conversation?.focusedAssignmentId ||
                  (!conversationId ? fullPageLaunch?.assignment_id : null),
              }}
              mode="full"
              conversationId={conversationId}
              initialMessages={conversation?.messages}
              initialRequest={conversation?.latestRequest}
            />
          )}
        </Paper>
      </Box>
    </FruitifyContainer>
  );
};

export default withPageTracking(AssistantPage, {
  feature: "assistant-page",
});
